import { useEffect, useState } from "react";

/**
 * useOrientationLock
 *
 * Intenta bloquear la orientación del dispositivo usando la Screen Orientation API.
 * Si el navegador no soporta el lock (ej: Safari iOS), activa un flag `showOverlay`
 * para que el componente muestre un fallback visual.
 *
 * @param {"portrait" | "any"} mode
 *   - "portrait": bloquea en vertical (páginas que NO deben rotar)
 *   - "any": desbloquea (permite cualquier orientación, ej: interior de curso)
 */
const useOrientationLock = (mode = "portrait") => {
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Usar window.screen para evitar el lint error no-restricted-globals
    const screenOrientation = window.screen?.orientation;

    if (mode === "portrait") {
      // Intento principal: Screen Orientation API
      const tryLock = async () => {
        try {
          if (screenOrientation?.lock) {
            await screenOrientation.lock("portrait");
            setShowOverlay(false);
          } else {
            // No soportado: activar fallback visual
            setShowOverlay(true);
          }
        } catch (err) {
          // lock() puede rechazarse si no está en fullscreen o en PWA
          setShowOverlay(true);
        }
      };

      tryLock();

      // Fallback: escuchar cambio de orientación para mostrar/ocultar overlay
      const handleChange = () => {
        const type = screenOrientation?.type || "";
        const isLandscape =
          type.includes("landscape") ||
          window.matchMedia("(orientation: landscape)").matches;
        setShowOverlay(isLandscape);
      };

      window.addEventListener("orientationchange", handleChange);
      // Check inicial
      handleChange();

      return () => {
        window.removeEventListener("orientationchange", handleChange);
        // Desbloquear al desmontar para no afectar otras páginas
        try {
          if (screenOrientation?.unlock) {
            screenOrientation.unlock();
          }
        } catch (_) {}
      };
    }

    if (mode === "any") {
      // Desbloquear para permitir rotación libre
      try {
        if (screenOrientation?.unlock) {
          screenOrientation.unlock();
        }
      } catch (_) {}
      setShowOverlay(false);

      return () => {
        // Al salir del curso, volver a bloquear en portrait
        try {
          if (screenOrientation?.lock) {
            screenOrientation.lock("portrait").catch(() => {});
          }
        } catch (_) {}
      };
    }
  }, [mode]);

  return { showOverlay };
};

export default useOrientationLock;
